import '@/css/style.scss';
import '@/css/map.scss';
import '@/css/classes/modal.scss';
import '@/css/ctxmenu.scss';

import {
    initServiceWorker,
    jsRefresh,
    unixTimestampToReadableDate,
} from '@/js/util.js';

import {
    assertEquals,
    assertIn,
} from '@/js/util/asserts.js';

import {
    ErrorModal,
    InfoModal,
    ConfirmationModal,
    InputModal,
} from '@/js/classes/modal.js';

import { concurrencyLimitedFetch, getUrlParam } from '@/js/util/shared_functionality.js';
import { DataDisplay } from '@/js/classes/datadisplay.js';
import { MapPageExternal } from '@/js/classes/map_page/map_page_external.js';

import {
    init as sentryInit,
    captureConsoleIntegration as sentryCaptureConsoleIntegration,
    captureException as sentryCaptureException,
} from '@sentry/browser';

import localforage from 'localforage';


const main_elem = document.querySelector('main');
const localforage_store = localforage.createInstance({ name: 'newcollector_shared_maps' });
let current_map_page = null;
const is_external_access = !(window.location.href.includes('map_external.html'));


async function displayMap(shared_map_data) {
    if(current_map_page && current_map_page.map) {
        current_map_page.map.setTarget(null);
        current_map_page.map.dispose();
        current_map_page = null;
    }

    main_elem.innerHTML = '';

    const map_container = document.createElement('div');
    map_container.id = 'map';
    map_container.style.width = '100%';
    map_container.style.height = '100%';
    main_elem.appendChild(map_container);

    try {
        current_map_page = new MapPageExternal(shared_map_data);
        await current_map_page.mapSetup();
        await current_map_page.setInitialMapCenter();
        await current_map_page.main();

    } catch(err) {
        console.log('Error setting up map:', err);
        sentryCaptureException(err);
        main_elem.innerHTML = '';
        new ErrorModal(`
            <h4>Map setup failed.</h4>
            <h5>Refresh the page to try again.</h5>
        `).show();
        throw err;
    }
}

async function initialPageLoad() {
    /* set dark/light mode */ {
        const prefersDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
        const theme = prefersDarkMode ? 'dark' : 'light';
        document.documentElement.setAttribute('data-bs-theme', theme);
    }

    if(is_external_access) { document.getElementById('titles-container').href = '/'; }

    function setMapUrlParam(share_code) {
        const url = new URL(window.location.href);

        if(is_external_access) {
            url.searchParams.delete('share_code');
            url.pathname = '/' + share_code;
        } else {
            url.searchParams.set('share_code', share_code);
        }

        window.history.replaceState(null, '', url.toString());
    }

    function removeMapUrlParam() {
        const url = new URL(window.location.href);

        if(is_external_access) {
            url.searchParams.delete('share_code');
            url.pathname = '/';
        } else {
            url.searchParams.delete('share_code');
        }

        window.history.replaceState(null, '', url.toString());
    }

    function getPathShareCode() {
        const path = window.location.pathname.replace(/^\/|\/$/g, '');
        const result = (path === '' || path === 'map_external.html') ? '' : path;
        if(!result) { return ''; }
        return result;
    }

    function isAlphanumeric(str) {
        return /^[a-zA-Z0-9]+$/.test(str);
    }

    function isValidShareCode(share_code) {
        if(!share_code) { return false; }
        share_code = share_code.toUpperCase();
        if(share_code.length !== 6) { return false; }
        if(!isAlphanumeric(share_code)) { return false; }
        return true;
    }

    async function tryShareCode(share_code) {
        // Returns boolean if succeeded or not
        share_code = share_code.toUpperCase();

        const shared_map_data_from_device = await localforage_store.getItem(share_code);
        if(shared_map_data_from_device) {
            console.log('Retrieved shared_map_data_from_device');
            setMapUrlParam(share_code);
            await displayMap(shared_map_data_from_device);
            return true;

        } else {
            try {
                const response = await concurrencyLimitedFetch(EXTERNAL_S3_URL+share_code+'.json');
                if(!response.ok) { return false; }

                const shared_map_data_from_s3 = await response.json();

                console.log('Retrieved shared_map_data_from_s3');
                assertIn('share_code', shared_map_data_from_s3);
                assertIn('assets', shared_map_data_from_s3);
                assertIn('project_info', shared_map_data_from_s3);
                assertEquals(shared_map_data_from_s3['share_code'], share_code);
                await localforage_store.setItem(share_code, shared_map_data_from_s3);

                setMapUrlParam(share_code);
                await displayMap(shared_map_data_from_s3);
                return true;
            } catch(err) {
                sentryCaptureException(err);
                return false;
            }
        }
    }


    const url_share_code = is_external_access ? getPathShareCode() : getUrlParam('share_code');
    if(url_share_code !=='') {
        if(isValidShareCode(url_share_code)) {
            if(await tryShareCode(url_share_code)) { return; }
            new ErrorModal(`Incorrect or expired share code.`).show();
        } else {
            new ErrorModal(`Invalid share code format.`).show();
        }
    }

    removeMapUrlParam();

    let datadisplay;

    main_elem.innerHTML = `
        <br>
        <div class="datadisplay-container sizing-container">
            <h5>Load a new shared map:</h5>
            <center>
                <button id="enter-share-code-button" class="btn btn-info m-1" style="width: 250px;">Enter a Share Code</button>
                <button id="upload-share-file-button" class="btn btn-info m-1" style="width: 250px;">Upload a Share File</button>
                <button id="upload-project-backup-button" class="btn btn-info m-1" style="width: 250px;">Upload a Project Backup</button>
            </center>
        </div>
        <hr>
        <br>
        <div class="datadisplay-container sizing-container">
            <h5>Or select a previously loaded map from your device:</h5>
            <div id="previously-loaded-maps"></div>
            <br>
            <br>
            <center>
                <button id="clear-saved-maps" class="btn btn-danger btn-sm">Clear Saved Maps</button>
            </center>
        </div>
    `;

    /* setup and populate datadisplay */ {
        const all_offline_data = {};
        await localforage_store.iterate(function(value, key, iterationNumber) { all_offline_data[key] = value; });

        datadisplay = new DataDisplay({
            'parent_elem': main_elem.querySelector('#previously-loaded-maps'),
            'name': 'Shared Map',
            'sort_by': 'shared',
            'field_names': ['Code', 'ID', 'Project Name', 'Map Name', 'Location', 'Count', 'Shared'],
            'entries_per_page': 20,
            'field_width_overrides': {
                'Code': '0 0 100px',
                'ID': '0 0 90px',
                'Count': '0 0 70px',
                'Shared': '0 0 210px',
            },
        });
        datadisplay.setup();

        for(const [share_code, shared_map_data] of Object.entries(all_offline_data)) {
            datadisplay.addEntry(share_code, [
                share_code,
                shared_map_data['project_info']['id'],
                shared_map_data['project_info']['name'],
                shared_map_data['map_name'] ?? 'Unnamed',
                shared_map_data['project_info']['location'],
                shared_map_data['assets'].length,
                unixTimestampToReadableDate(shared_map_data['timestamp_generated']),
            ], [
                share_code,
                shared_map_data['project_info']['id'],
                shared_map_data['project_info']['name'],
                shared_map_data['map_name'] ?? 'Unnamed',
                shared_map_data['project_info']['location'],
                shared_map_data['assets'].length,
                shared_map_data['timestamp_generated'],
            ]);
        }

        datadisplay.onEntryClick(async (share_code) => {
            setMapUrlParam(share_code);
            await displayMap(all_offline_data[share_code]);

        });

        datadisplay.afterEntries();
    }

    /* handle buttons */ {
        const enter_share_code_button = main_elem.querySelector('#enter-share-code-button');
        const upload_share_file_button = main_elem.querySelector('#upload-share-file-button');
        const upload_project_backup_button = main_elem.querySelector('#upload-project-backup-button');
        const clear_saved_maps_button = main_elem.querySelector('#clear-saved-maps');

        enter_share_code_button.addEventListener('click', async () => {
            const enter_modal = new InputModal(
                'Enter Share Code',
                '',
                'Load',
                'AB12CD34'
            );

            enter_modal.onConfirm(async (share_code) => {
                if(isValidShareCode(share_code)) {
                    if(await tryShareCode(share_code)) {
                        enter_modal.remove();
                        return;
                    }
                    enter_modal.appendHtml(`Incorrect or expired share code.`);
                } else {
                    enter_modal.appendHtml(`Invalid share code format.`);
                }
            });

            enter_modal.show();
        });

        upload_share_file_button.addEventListener('click', async () => {
            const upload_modal = new ConfirmationModal('Upload Share File');

            const file_elem = document.createElement('input');
            file_elem.classList.add('w-100', 'm-1');
            file_elem.type = 'file';
            upload_modal.appendNode(file_elem);

            upload_modal.onConfirm(async () => {
                if(file_elem.files.length === 0) {
                    new ErrorModal('<h5>No file selected.</h5>').show();
                    upload_modal.remove();
                    return;
                }

                const file = file_elem.files[0];

                if(!file) {
                    new ErrorModal('<h5>Invalid file selected.</h5>').show();
                    upload_modal.remove();
                    return;
                }

                try {
                    const shared_map_data = JSON.parse(await file.text());
                    assertIn('share_code', shared_map_data);
                    assertIn('assets', shared_map_data);
                    assertIn('project_info', shared_map_data);

                    await localforage_store.setItem(shared_map_data['share_code'].toUpperCase(), shared_map_data);
                    setMapUrlParam(shared_map_data['share_code']);
                    await displayMap(shared_map_data);
                    upload_modal.remove();

                } catch(err) {
                    console.log(err);
                    sentryCaptureException(err);
                    new ErrorModal(`<strong>Error reading import file:</strong> ${err.message}`).show();
                    upload_modal.remove();
                }
            });

            upload_modal.show();
        });

        upload_project_backup_button.addEventListener('click', async () => {
            new InfoModal('This feature not yet implemented.').show();
        });

        clear_saved_maps_button.addEventListener('click', async () => {
            const modal = new ConfirmationModal(
                'Clear Saved Maps',
                `
                    <h5>Are you sure you want to delete all saved maps from your device?</h5>
                    You will not be able to view these maps again, unless you reenter the share code or click the link again.
                `
            );

            modal.onConfirm(() => {
                modal.remove();
                localforage_store.clear();
                jsRefresh();
            });

            modal.show();
        });
    }
}


document.title = 'Tree Collector Viewer';

sentryInit({
    dsn: EXTERNAL_SENTRY_DSN,
    release: BUILD_UID,
    environment: build_mode,
    sendDefaultPii: true,
    sampleRate: 1.0,
    tracesSampleRate: 1.0,
    enableLogs: true,
    replaysSessionSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,
    attachStacktrace: true,
    integrations: [
        sentryCaptureConsoleIntegration({
            levels: ['error'],
        }),
    ],
});

if(build_mode !== 'locallocal') { initServiceWorker(); }

await initialPageLoad();